import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { COUNTRIES } from "@/lib/brand";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-white text-sm focus:border-[#E6B34A] focus:outline-none";
const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-zinc-400 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// Reusable edit modal for an existing event. Calls PUT /events/:id.
export default function EventEditModal({ event, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const coverRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: event.title || "", event_type: event.event_type || "Service",
    starts_at: (event.starts_at || "").slice(0, 16), country: event.country || "",
    city: event.city || "", venue: event.venue || "", description: event.description || "",
    link: event.link || "", recurrence: event.recurrence || "none",
  });

  useEffect(() => { api.get("/events/meta").then((r) => setTypes(r.data.event_types || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at || !form.country) { toast.error("Title, date/time and country are required"); return; }
    setSaving(true);
    try {
      await api.put(`/events/${event.id}`, form);
      if (coverFile) {
        const fd = new FormData(); fd.append("file", coverFile);
        try { await api.post(`/events/${event.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) {}
      }
      toast.success("Event updated");
      setSaving(false);
      onSaved && onSaved();
      onClose();
    } catch (err) { setSaving(false); toast.error(err?.response?.data?.detail || "Could not update event"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="event-edit-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#141C42] p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl">Edit event</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Event title *">
            <input data-testid="edit-event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Type">
              <select data-testid="edit-event-type" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className={inputCls}>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Date & time *">
              <input data-testid="edit-event-datetime" type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={`${inputCls} min-w-0`} />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Country *">
              <select data-testid="edit-event-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls}>
                <option value="">Select…</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="City">
              <input data-testid="edit-event-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
            </Field>
          </div>
          <Field label="Venue">
            <input data-testid="edit-event-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Link (livestream / registration)">
            <input data-testid="edit-event-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputCls} placeholder="https://…" />
          </Field>
          <Field label="Description">
            <textarea data-testid="edit-event-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Repeats">
              <select data-testid="edit-event-recurrence" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} className={inputCls}>
                {RECURRENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Replace cover (optional)">
              <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="edit-event-cover-file" onChange={(e) => setCoverFile(e.target.files[0])} />
              <button type="button" onClick={() => coverRef.current?.click()} data-testid="edit-event-cover-btn" className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-sm text-zinc-300 hover:border-[#E6B34A] transition-colors">
                <ImagePlus className="w-4 h-4 text-[#E6B34A]" /> {coverFile ? coverFile.name : "Change cover photo"}
              </button>
            </Field>
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="edit-event-submit" className="w-full mt-5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save changes"}
        </button>
      </form>
    </div>
  );
}
